// client\src\pages\ContentViewer\Completed.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from './Navbar';
import { getCertificateByCourse } from '../../services/certificateService';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Box, TextField, IconButton, Tooltip, Typography, CircularProgress, Button } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

function Completed() {
    const { courseSlug } = useParams();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [certificate, setCertificate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCertificate = async () => {
            setLoading(true);
            const response = await getCertificateByCourse(courseSlug);
            if (response.success) {
                setCertificate(response.certificate);
            } else {
                setError(response.message);
            }
            setLoading(false);
        };

        fetchCertificate();
    }, [courseSlug]);

    const toggleDrawer = () => {
        setIsDrawerOpen((prev) => !prev);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* Navbar */}
            <Navbar courseTitle={certificate ? certificate.course.title : ''} toggleDrawer={toggleDrawer} />

            {/* Content */}
            <Box
                sx={{
                    display: 'flex',
                    flexGrow: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    backgroundColor: '#ffffff',
                }}
            >
                {loading ? (
                    <CircularProgress />
                ) : error ? (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Typography variant="h5" fontWeight="bold" color="error" sx={{ mb: 2 }}>
                            Certificate Not Found
                        </Typography>
                        <Typography sx={{ mb: 3 }}>
                            We couldn’t find a certificate for this course.
                            Please ensure the course exists and that you have completed all lessons.
                        </Typography>
                        <Button variant="contained" size='large' color="primary" onClick={() => window.history.back()}>
                            Go Back
                        </Button>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            flexGrow: 1,
                            padding: '2rem',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                            backgroundColor: '#ffffff',
                        }}
                    >
                        {/* Celebration Image */}
                        <Box
                            component="img"
                            src="/static/img/completed.png"
                            alt="Completed"
                            sx={{
                                height: '120px',
                                width: 'auto',
                                marginBottom: '1.5rem',
                                animation: 'bounce 2s infinite',
                                '@keyframes bounce': {
                                    '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0)' },
                                    '40%': { transform: 'translateY(-20px)' },
                                    '60%': { transform: 'translateY(-10px)' },
                                },
                            }}
                        />

                        {/* Congratulatory Text */}
                        <Box
                            sx={{
                                fontSize: '1.6rem',
                                fontWeight: 'bold',
                                marginBottom: '1rem',
                                color: '#0d47a1',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                            }}
                        >
                            Congrats, Course Completed!
                        </Box>
                        <Typography
                            sx={{
                                color: 'text.secondary',
                                fontSize: { xs: '0.8rem', sm: '0.8rem', md: '0.85rem', lg: '0.9rem' },
                                lineHeight: { xs: 1.4, sm: 1.6, md: 1.8 },
                                maxWidth: { xs: '100%', sm: '66.6667%', md: '66.6667%', lg: '50%' },
                                marginBottom: '2rem',
                            }}
                        >
                            Congratulations on successfully completing all the lessons!
                            Keep pushing forward, and continue to apply this knowledge to reach greater heights!
                        </Typography>

                        {/* Action Buttons */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '1rem',
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                                marginBottom: '2rem',
                            }}
                        >
                            {/* LinkedIn Share Button */}
                            <Button
                                variant="outlined"
                                startIcon={<LinkedInIcon />}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '0.8rem',
                                    borderRadius: '6px',
                                    padding: '0.6rem 1.5rem',
                                    color: '#0d47a1',
                                    borderColor: '#0d47a1',
                                    '&:hover': {
                                        backgroundColor: '#f0f0f0',
                                    },
                                }}
                                onClick={() => {
                                    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                                        `${window.location.origin}/certificate/view/${certificate.csnId}`
                                    )}`;
                                    window.open(shareUrl, '_blank', 'noopener,noreferrer');
                                }}
                            >
                                Share on LinkedIn
                            </Button>

                            {/* Download Certificate Button */}
                            <Button
                                variant="contained"
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '0.8rem',
                                    borderRadius: '6px',
                                    padding: '0.6rem 1.5rem',
                                    backgroundColor: '#0d47a1',
                                    color: '#ffffff',
                                    '&:hover': {
                                        backgroundColor: '#002171',
                                    },
                                }}
                                onClick={() => {
                                    if (certificate?.imageUrl) {
                                        const link = document.createElement('a');
                                        link.href = certificate.imageUrl;
                                        link.download = `${certificate.csnId}.png`;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                    }
                                }}
                            >
                                Download Certificate
                            </Button>

                        </Box>

                        {/* Certificate Link Section */}
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: '600px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                py: '2rem',
                                px: '2.5rem',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <Typography sx={{ mb: 1 }}>
                                Here’s your certificate link. You can copy it and share it directly:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <TextField
                                    value={`${window.location.origin}/certificate/view/${certificate.csnId}`}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                    sx={{
                                        flex: 1,
                                        backgroundColor: '#ffffff',
                                        borderRadius: '4px 0 0 4px',
                                    }}
                                />
                                <Tooltip title="Copy link" arrow>
                                    <IconButton
                                        onClick={() =>
                                            navigator.clipboard.writeText(
                                                `${window.location.origin}/certificate/view/${certificate.csnId}`
                                            )
                                        }
                                        sx={{
                                            padding: '0.5rem',
                                            backgroundColor: '#0d47a1',
                                            color: '#ffffff',
                                            borderRadius: '0 4px 4px 0',
                                            '&:hover': { backgroundColor: '#002171' },
                                        }}
                                    >
                                        <ContentCopyIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default Completed;
